import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Loader2, BookOpen, Sparkles, ToggleLeft, ToggleRight, Menu, X } from 'lucide-react'
import { browseTaxonomy, searchResources } from '../../services/api'
import type { TaxonomyNode, ResourceType } from '../../types'
import TaxonomySwitcher from '../../components/browse/TaxonomySwitcher'
import TaxonomyTree from '../../components/browse/TaxonomyTree'
import SubjectCard from '../../components/browse/SubjectCard'
import BreadcrumbNav from '../../components/browse/BreadcrumbNav'
import { RESOURCE_TYPE_COLORS } from '../../constants/colors'
import { RESOURCE_TYPES } from '../../constants/resourceTypes'

interface BreadcrumbItem {
  nodeId: string
  label: string
}

export default function Browse() {
  const [activeTaxonomy, setActiveTaxonomy] = useState('anzsrc-2020')
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>(undefined)
  const [selectedNodeLabel, setSelectedNodeLabel] = useState<string | undefined>(undefined)
  const [breadcrumbPath, setBreadcrumbPath] = useState<BreadcrumbItem[]>([])
  const [showAiConnections, setShowAiConnections] = useState(false)
  const [mobileTreeOpen, setMobileTreeOpen] = useState(false)

  // Fetch root-level taxonomy nodes
  const {
    data: rootNodes,
    isLoading: rootLoading,
  } = useQuery({
    queryKey: ['taxonomy-browse', activeTaxonomy],
    queryFn: () => browseTaxonomy(activeTaxonomy),
  })

  // Fetch children of the selected node (if any) for the right panel
  const {
    data: childNodes,
    isLoading: childrenLoading,
  } = useQuery({
    queryKey: ['taxonomy-children', activeTaxonomy, selectedNodeId],
    queryFn: () => browseTaxonomy(activeTaxonomy, selectedNodeId),
    enabled: !!selectedNodeId,
  })

  // Fetch resources for selected subject
  const {
    data: subjectResources,
    isLoading: resourcesLoading,
  } = useQuery({
    queryKey: ['taxonomy-resources', selectedNodeLabel, showAiConnections],
    queryFn: () =>
      searchResources({
        query: selectedNodeLabel || '',
        taxonomy_filter: activeTaxonomy,
        embeddings_mode: showAiConnections,
        page: 1,
        page_size: 20,
      }),
    enabled: !!selectedNodeLabel,
  })

  // Handle taxonomy switch
  const handleTaxonomySwitch = useCallback((taxonomyId: string) => {
    setActiveTaxonomy(taxonomyId)
    setSelectedNodeId(undefined)
    setSelectedNodeLabel(undefined)
    setBreadcrumbPath([])
  }, [])

  // Handle node selection in tree
  const handleNodeSelect = useCallback((node: TaxonomyNode) => {
    setSelectedNodeId(node.node_id)
    setSelectedNodeLabel(node.label)

    // Update breadcrumb: find or add this node
    setBreadcrumbPath(prev => {
      const existingIndex = prev.findIndex(b => b.nodeId === node.node_id)
      if (existingIndex >= 0) {
        return prev.slice(0, existingIndex + 1)
      }
      return [...prev, { nodeId: node.node_id, label: node.label }]
    })

    // Close mobile tree after selection
    setMobileTreeOpen(false)
  }, [])

  // Handle breadcrumb navigation
  const handleBreadcrumbNavigate = useCallback((nodeId: string | null) => {
    if (nodeId === null) {
      setSelectedNodeId(undefined)
      setSelectedNodeLabel(undefined)
      setBreadcrumbPath([])
    } else {
      const index = breadcrumbPath.findIndex(b => b.nodeId === nodeId)
      if (index >= 0) {
        const item = breadcrumbPath[index]
        setSelectedNodeId(item.nodeId)
        setSelectedNodeLabel(item.label)
        setBreadcrumbPath(breadcrumbPath.slice(0, index + 1))
      }
    }
  }, [breadcrumbPath])

  // Handle card click to drill into a child node
  const handleSubjectCardClick = useCallback((node: TaxonomyNode) => {
    setSelectedNodeId(node.node_id)
    setSelectedNodeLabel(node.label)
    setBreadcrumbPath(prev => [...prev, { nodeId: node.node_id, label: node.label }])
  }, [])

  // Handle tree expand (lazy loading children)
  const handleTreeExpand = useCallback(async (node: TaxonomyNode): Promise<TaxonomyNode[]> => {
    return browseTaxonomy(activeTaxonomy, node.node_id)
  }, [activeTaxonomy])

  // Taxonomy label for breadcrumb
  const taxonomyLabel = useMemo(() => {
    const labels: Record<string, string> = {
      'anzsrc-2020': 'ANZSRC 2020',
      'openalex-topics': 'OpenAlex Topics',
      'isced-f-2013': 'ISCED-F 2013',
      'cip-2020': 'CIP 2020',
      'ddc-summaries': 'DDC Summaries',
    }
    return labels[activeTaxonomy] || activeTaxonomy
  }, [activeTaxonomy])

  // Determine which nodes to show in the right panel
  const displayNodes = selectedNodeId ? childNodes : rootNodes
  const isDisplayLoading = selectedNodeId ? childrenLoading : rootLoading

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-acad-500" />
            <h1 className="text-xl font-bold text-gray-900">Browse Taxonomies</h1>
          </div>

          {/* AI connections toggle */}
          <button
            onClick={() => setShowAiConnections(!showAiConnections)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium
                       transition-colors ${
                         showAiConnections
                           ? 'bg-purple-50 text-purple-700 border border-purple-200'
                           : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-gray-300'
                       }`}
          >
            {showAiConnections ? (
              <ToggleRight className="w-4 h-4" />
            ) : (
              <ToggleLeft className="w-4 h-4" />
            )}
            <Sparkles className={`w-3 h-3 ${showAiConnections ? 'text-purple-500' : 'text-gray-400'}`} />
            AI-discovered connections
          </button>
        </div>

        {/* Taxonomy switcher */}
        <TaxonomySwitcher
          activeTaxonomyId={activeTaxonomy}
          onSwitch={handleTaxonomySwitch}
        />

        {/* Breadcrumb */}
        {breadcrumbPath.length > 0 && (
          <div className="mt-2 -mx-1">
            <BreadcrumbNav
              path={breadcrumbPath}
              taxonomyLabel={taxonomyLabel}
              onNavigate={handleBreadcrumbNavigate}
            />
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile tree toggle */}
        <button
          onClick={() => setMobileTreeOpen(!mobileTreeOpen)}
          className="lg:hidden fixed bottom-4 left-4 z-30 w-12 h-12 bg-acad-500 text-white
                     rounded-full shadow-lg flex items-center justify-center
                     hover:bg-acad-600 transition-colors"
        >
          {mobileTreeOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Left panel: Taxonomy Tree */}
        <div className={`
          ${mobileTreeOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 transition-transform duration-200
          fixed lg:relative inset-y-0 left-0 z-20 lg:z-auto
          w-72 lg:w-72 xl:w-80 bg-white border-r border-gray-200
          flex flex-col overflow-hidden shrink-0
        `}>
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Subject Areas
            </h2>
            {rootNodes && (
              <span className="text-[10px] text-gray-400">
                {rootNodes.length} top-level
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {rootLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 text-acad-500 animate-spin" />
              </div>
            ) : rootNodes && rootNodes.length > 0 ? (
              <TaxonomyTree
                nodes={rootNodes}
                onNodeSelect={handleNodeSelect}
                selectedNodeId={selectedNodeId}
                onExpand={handleTreeExpand}
              />
            ) : (
              <div className="p-4 text-center text-sm text-gray-400">
                No taxonomy data available
              </div>
            )}
          </div>
        </div>

        {/* Mobile overlay backdrop */}
        {mobileTreeOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-10 lg:hidden"
            onClick={() => setMobileTreeOpen(false)}
          />
        )}

        {/* Right panel: Content area */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {!selectedNodeId ? (
            /* No selection: show root-level subject cards */
            <div className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                {taxonomyLabel}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Select a subject area to explore resources, or navigate the tree on the left.
              </p>
              {isDisplayLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-acad-500 animate-spin" />
                </div>
              ) : displayNodes && displayNodes.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {displayNodes.map(node => (
                    <SubjectCard
                      key={node.node_id}
                      node={node}
                      onClick={handleSubjectCardClick}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400 text-sm">
                  No subject areas found for this taxonomy.
                </div>
              )}
            </div>
          ) : (
            /* Node selected: show child subjects and resources */
            <div className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                {selectedNodeLabel}
              </h2>

              {/* Child subject areas */}
              {childrenLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 text-acad-500 animate-spin" />
                </div>
              ) : childNodes && childNodes.length > 0 ? (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    Sub-areas ({childNodes.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {childNodes.map(node => (
                      <SubjectCard
                        key={node.node_id}
                        node={node}
                        onClick={handleSubjectCardClick}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Resources for this subject */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-3 flex items-center gap-2">
                  Resources
                  {subjectResources && (
                    <span className="text-xs text-gray-400">
                      ({subjectResources.total} found)
                    </span>
                  )}
                  {showAiConnections && (
                    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full
                                     bg-purple-50 text-purple-600">
                      <Sparkles className="w-3 h-3" />
                      AI-enriched
                    </span>
                  )}
                </h3>
                {resourcesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-acad-500 animate-spin" />
                  </div>
                ) : subjectResources && subjectResources.results.length > 0 ? (
                  <div className="space-y-3">
                    {subjectResources.results.map((result) => {
                      const resourceType = ('resource_type' in result
                        ? result.resource_type
                        : 'journal') as ResourceType
                      const color = RESOURCE_TYPE_COLORS[resourceType] || '#6B7280'
                      const typeLabel = RESOURCE_TYPES.find(r => r.id === resourceType)?.label || resourceType
                      const title = result.title
                      const hasCrossDomain = result.cross_domain_subjects && result.cross_domain_subjects.length > 0

                      return (
                        <div
                          key={'record_id' in result ? result.record_id : result.resource_id}
                          className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm
                                     hover:border-acad-200 transition-all"
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className="w-2 h-full min-h-[40px] rounded-full shrink-0 mt-0.5"
                              style={{ backgroundColor: color }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span
                                  className="text-[10px] font-semibold uppercase px-1.5 py-0.5
                                             rounded text-white"
                                  style={{ backgroundColor: color }}
                                >
                                  {typeLabel}
                                </span>
                                {hasCrossDomain && showAiConnections && (
                                  <span className="flex items-center gap-1 text-[10px] px-1.5
                                                   py-0.5 rounded bg-purple-50 text-purple-600">
                                    <Sparkles className="w-2.5 h-2.5" />
                                    Cross-domain
                                  </span>
                                )}
                                {result._score > 0 && (
                                  <span className="text-[10px] text-gray-400">
                                    Score: {(result._score * 100).toFixed(0)}%
                                  </span>
                                )}
                              </div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                                {title}
                              </h4>
                              {'abstract' in result && result.abstract && (
                                <p className="text-xs text-gray-500 line-clamp-2">
                                  {result.abstract}
                                </p>
                              )}
                              {'description' in result && result.description && (
                                <p className="text-xs text-gray-500 line-clamp-2">
                                  {result.description}
                                </p>
                              )}
                              {'authors' in result && result.authors.length > 0 && (
                                <p className="text-xs text-gray-400 mt-1">
                                  {result.authors.slice(0, 3).join(', ')}
                                  {result.authors.length > 3 && ` +${result.authors.length - 3} more`}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
                    <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No resources found for this subject.</p>
                    {!showAiConnections && (
                      <p className="text-xs text-gray-400 mt-1">
                        Try enabling AI-discovered connections for broader results.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
